import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as React from "react";
import {
    Alert,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    TextArea,
} from "@jhool-io/fe-components";
import {
    IGetClientNotes,
    ISessionNote,
    IEditCancellationNotePayload,
} from "../../../../modules/notes/types/notes.types";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import { CANCELLATION_NOTE_LABELS } from "../../../../utils/constants";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";

interface NoteCancellationProps {
    noteDetails: ISessionNote;
}

export default function EditCancellation({
    noteDetails,
}: NoteCancellationProps) {
    const [showPrompt, setShowPrompt] = React.useState(false);
    const [selectedNote, setSelectedNote] = React.useState(
        {} as IGetClientNotes
    );

    const { handleAutoSaveNote, handleEditDraftNote, handleSignNote } =
        useEditNote();

    const isSigning = React.useRef(false);

    const {
        handleSubmit,
        getValues,
        register,
        setValue,
        watch,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditCancellationNotePayload>({
        resolver: yupResolver(
            yup.object().shape({
                reason: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Reason for cancellation is required"),
                    otherwise: yup.string(),
                }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content || undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    // Function to handle item click and set the selected note content
    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue("reason", note.note_content.reason);
        setShowPrompt(false);
    };

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <div>
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}

            <form
                aria-label="edit-form"
                onSubmit={handleSubmit(handleSignNote)}
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("reason")}
                        label={CANCELLATION_NOTE_LABELS.reason}
                        hasError={!!errors.reason}
                        errorText={errors.reason?.message}
                        defaultValue={noteDetails.note_content?.reason}
                        isRequired
                    />
                </div>

                {noteDetails.date_of_service && (
                    <Alert
                        type="warning"
                        description="Client will be charged for cancelling if you create this note."
                    />
                )}

                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </div>
    );
}
